import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { KbAnalyseItemResponse, KbDiseaseItemResponse } from '@medindex-webapi';
import { BreadcrumbItem } from '@shared/modules/breadcrumb/types/breadcrumbItem';
import {
  Subject,
  BehaviorSubject,
  Observable,
  filter,
  map,
  takeUntil,
  switchMap,
  throwError,
  catchError,
  first,
} from 'rxjs';
import { HelixBookRepositoryService } from '../../services/helix-book-repository.service';
import { ValidationPatterns } from '@shared/services/validation/validation-patterns.const';
import { DOCUMENT } from '@angular/common';
import { MetaService } from '@shared/services/meta/meta.service';
import { Location } from '@angular/common';
import { JsonLdStorage } from '@shared/services/meta/jsonld-schema-storage';
import { PlatformService } from '@shared/services/platform.service';
import { YmService } from '@shared/services/yandex-metrica/ym.service';
import { HelixRoutes } from '@shared/enums/routes';
import { YmGoalCatalogItem } from '../../../catalog/enums/ym-goal-catalog-item.enum';

@Component({
  selector: 'app-helixbook-item',
  templateUrl: './item.component.html',
  styleUrls: ['./item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HelixBookRepositoryService],
})
export class ItemComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private platformService: PlatformService,
    private route: ActivatedRoute,
    private repository: HelixBookRepositoryService,
    private metaService: MetaService,
    private location: Location,
    private ymService: YmService
  ) {}

  @HostListener('click', ['$event.target'])
  public onClick(element: HTMLElement): void {
    const url = (element as HTMLLinkElement)?.href?.toLowerCase();
    if (url && url.includes(HelixRoutes.CatalogItem)) {
      this.ymService.reachGoal(YmGoalCatalogItem.OpenFromHelixBook);
    }
  }

  observer: IntersectionObserver | null = null;
  ngUnsubscribe$ = new Subject<void>();
  isLoaded$ = new BehaviorSubject(false);
  showError$ = new BehaviorSubject(false);
  item$ = new BehaviorSubject<
    KbAnalyseItemResponse | KbDiseaseItemResponse | null
  >(null);
  breadcrumbs$: Observable<BreadcrumbItem[]> = this.item$.pipe(
    filter(
      (item): item is KbAnalyseItemResponse | KbDiseaseItemResponse => !!item
    ),
    map((item) => {
      return [
        { href: '/', text: 'Главная' },
        { href: '/kb', text: 'Helixbook' },
        { href: '', text: item?.title ?? '' },
      ];
    })
  );

  ngAfterViewInit() {
    this._initViewObserver();
  }

  ngOnInit(): void {
    this._loadItem();
    this._scrollToSubject();
  }

  onBack(): void {
    this.location.back();
  }

  private _loadItem(): void {
    this.route.params
      .pipe(
        switchMap((params) => {
          const id = (params.id as string) || '';

          if (!id) return throwError(() => new Error('KbItemNotFound'));

          if (id.match(new RegExp(ValidationPatterns.Hxid)))
            return this.repository.getAnalyseItem(id);

          if (parseInt(id)) return this.repository.getDiseaseItem(parseInt(id));

          return throwError(() => new Error('KbItemNotFound'));
        }),
        takeUntil(this.ngUnsubscribe$),
        catchError((err) => {
          this.isLoaded$.next(true);
          this.showError$.next(true);
          return throwError(() => err);
        })
      )
      .subscribe((item) => {
        this.item$.next(item);
        this.isLoaded$.next(true);

        this.metaService.title(item.title ?? item.metaInfo?.title ?? '');
        this.metaService.description(item.metaInfo?.description ?? '');
        this.metaService.keywords(item.metaInfo?.keywords ?? '');
        const jsonLd = JsonLdStorage.Article(item);
        this.metaService.insertJsonLdSchema(jsonLd);
      });
  }

  private _initViewObserver(): void {
    if (this.platformService.isServer) return;

    const options: IntersectionObserverInit = {
      threshold: 0.1,
      rootMargin: '0px 0px -90%',
    };
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const subjId = parseInt(entry.target.id.replace('subj', ''));
          this.repository.setActiveSubject(subjId);
        }
      });
    }, options);

    this.isLoaded$
      .pipe(
        filter((isLoaded) => isLoaded),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(() => {
        // when innerHTML is rendered
        setTimeout(() => {
          const subjs = this.document.querySelectorAll('.subj-header');
          subjs.forEach((el) => this.observer?.observe(el));
        }, 0);
      });
  }

  private _scrollToSubject(): void {
    this.repository.scrollEvent$
      .pipe(
        switchMap(() => this.repository.activeSubject$.pipe(first())),
        filter(
          (subjectId): subjectId is number =>
            subjectId !== undefined && this.platformService.isBrowser
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((subjectId) => {
        const element = this.document.getElementById(`subj${subjectId}`);
        element?.scrollIntoView({
          block: 'start',
          inline: 'nearest',
        });
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.observer?.disconnect();
    this.repository.setActiveSubject(undefined);
  }
}
